import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { subHours } from "date-fns";
import { Flame, Wheat, Beef, Cookie, Car, Info } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NutritionRings, type NutritionData } from "./NutritionRings";
import { MacroPieChart } from "./MacroPieChart";
import { User } from '@supabase/supabase-js';
import { useEffect, useState } from "react";

const DAILY_GOALS = {
  calories: 2000,
  carbs: 275, 
  protein: 180,
  fat: 67,
};

export function AnalyticsDaily() {
  const [user, setUser] = useState<User | null>(null);
  
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getUser();
  }, []);

  const { data: nutritionData, isLoading } = useQuery({
    queryKey: ["daily-nutrition"],
    queryFn: async () => {
      const twentyFourHoursAgo = subHours(new Date(), 24);
      
      const { data: entries, error } = await supabase
        .from("v_food_entries_aggregated")
        .select("total_calories, total_carbs, total_protein, total_fat")
        .eq('user_id', user?.id)
        .gte("consumed_at", twentyFourHoursAgo.toISOString());

      if (error) throw error;

      if (!entries?.length) {
        return null;
      }

      const totals = entries.reduce(
        (acc, entry) => ({
          calories: acc.calories + (entry.total_calories || 0),
          carbs: acc.carbs + (entry.total_carbs || 0),
          protein: acc.protein + (entry.total_protein || 0),
          fat: acc.fat + (entry.total_fat || 0),
        }),
        { calories: 0, carbs: 0, protein: 0, fat: 0 }
      );

      return [
        {
          nutrient: "Fat",
          label: `${Math.round(totals.fat)}g fat`,
          value: Math.min(Math.round(((totals.fat || 0) / DAILY_GOALS.fat) * 100), 100),
          fill: "hsl(214, 84%, 53%)",
          total: Math.round(totals.fat) || 0
        },
        {
          nutrient: "Protein",
          label: `${Math.round(totals.protein)}g protein`,
          value: Math.min(Math.round(((totals.protein || 0) / DAILY_GOALS.protein) * 100), 100),
          fill: "hsl(0, 84%, 53%)",
          total: Math.round(totals.protein) || 0
        },
        {
          nutrient: "Carbs",
          label: `${Math.round(totals.carbs)}g carbs`,
          value: Math.min(Math.round(((totals.carbs || 0) / DAILY_GOALS.carbs) * 100), 100),
          fill: "hsl(47, 96%, 53%)",
          total: Math.round(totals.carbs) || 0
        },
        {
          nutrient: "Calories",
          label: `${Math.round(totals.calories)} cal`,
          value: Math.min(Math.round(((totals.calories || 0) / DAILY_GOALS.calories) * 100), 100),
          fill: "hsl(24, 96%, 53%)",
          total: Math.round(totals.calories) || 0
        },
      ];
    },
    enabled: !!user
  });

  const { data: macroData, isLoading: isMacroLoading } = useQuery({
    queryKey: ["daily-macros-pie"],
    queryFn: async () => {
      const twentyFourHoursAgo = subHours(new Date(), 24);
      
      const { data: entries, error } = await supabase
        .from("v_food_entry_ingredients_detailed")
        .select("calories, carbs_grams, protein_grams, fat_grams")
        .eq('user_id', user?.id)
        .gte("created_at", twentyFourHoursAgo.toISOString());

      if (error) throw error;

      if (!entries?.length) {
        return {
          chartData: [],
          totalCalories: 0
        };
      }

      const totals = entries.reduce(
        (acc, entry) => ({
          calories: acc.calories + (Number(entry.calories) || 0),
          carbs: acc.carbs + (Number(entry.carbs_grams) || 0),
          protein: acc.protein + (Number(entry.protein_grams) || 0),
          fat: acc.fat + (Number(entry.fat_grams) || 0),
        }),
        { calories: 0, carbs: 0, protein: 0, fat: 0 }
      );

      // Calculate calories using Atwater factors
      const carbCalories = totals.carbs * 4; // 4 kcal/g for carbs
      const proteinCalories = totals.protein * 4; // 4 kcal/g for protein  
      const fatCalories = totals.fat * 9; // 9 kcal/g for fat
      const atwaterTotalCalories = carbCalories + proteinCalories + fatCalories;

      const chartData = [
        {
          name: "Carbs",
          value: Math.round((carbCalories / atwaterTotalCalories) * 100),
          fill: "hsl(47, 96%, 53%)"
        },
        {
          name: "Protein", 
          value: Math.round((proteinCalories / atwaterTotalCalories) * 100),
          fill: "hsl(0, 84%, 53%)"
        },
        {
          name: "Fat",
          value: Math.round((fatCalories / atwaterTotalCalories) * 100), 
          fill: "hsl(214, 84%, 53%)"
        },
      ];

      return {
        chartData,
        totalCalories: Math.round(totals.calories),
        atwaterTotalCalories: Math.round(atwaterTotalCalories)
      };
    },
    enabled: !!user
  });

  if (isLoading || !nutritionData || isMacroLoading || !macroData) {
    return null;
  }

  return (
    <>
      <Card className="flex flex-col">
        <CardHeader className="items-center pb-2">
          <CardTitle className="flex items-center justify-between">
            <span className="flex-1">Goal Rings</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="flex items-center justify-center w-6 h-6">
                  <span className="flex items-center justify-center">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="px-3 py-1.5">
                  <p>Marcos totals based on recommended 2000 calorie diet Daily Values (DV)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>Last 24 Hours</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 pb-4">
          <NutritionRings data={nutritionData} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="flex items-center gap-2 bg-orange-50 p-3 rounded-lg">
              <Flame className="h-5 w-5 text-orange-500" />
              <div>
                <p className="text-sm font-medium">{Math.round(nutritionData[3]?.total || 0)} cal</p>
                <p className="text-xs text-muted-foreground">of {DAILY_GOALS.calories} cal ({nutritionData[3]?.value || 0}%)</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-yellow-50 p-3 rounded-lg">
              <Wheat className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium">{Math.round(nutritionData[2]?.total || 0)}g</p>
                <p className="text-xs text-muted-foreground">of {DAILY_GOALS.carbs}g ({nutritionData[2]?.value || 0}%)</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-red-50 p-3 rounded-lg">
              <Beef className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-sm font-medium">{Math.round(nutritionData[1]?.total || 0)}g</p>
                <p className="text-xs text-muted-foreground">of {DAILY_GOALS.protein}g ({nutritionData[1]?.value || 0}%)</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg">
              <Cookie className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm font-medium">{Math.round(nutritionData[0]?.total || 0)}g</p>
                <p className="text-xs text-muted-foreground">of {DAILY_GOALS.fat}g ({nutritionData[0]?.value || 0}%)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="flex flex-col mt-4">
        <CardHeader className="items-center pb-2">
          <CardTitle>Calories by Macro %</CardTitle>
          <CardDescription>Last 24 Hours</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 pb-4">
          <MacroPieChart data={macroData.chartData} totalCalories={macroData.totalCalories} atwaterTotalCalories={macroData.atwaterTotalCalories} />
        </CardContent>
      </Card>
    </>
  );
}
